import {
  AHNonAnimtedPageIndicator,
  Box,
  ColorType,
  useRadiusSize,
  useTheme,
} from "arkh";
import React, { useCallback, useMemo, useState } from "react";

import { BaseWidgetProps } from "../BaseProps";
import { View } from "react-native";
import { WebPageWrapper } from "./WebPageWrapper";

import { WidgetNotePage } from "./WidgetNotePage";

import { useInterval } from "../../useInterval";
import { useWidgetLayout } from "../WidgetLayoutContext";

import { graphql, useFragment } from "react-relay";

import { useNoteSubscriptions } from "./useNoteSubscriptions";
import { NoteWeb_note$key } from "./__generated__/NoteWeb_note.graphql";
import { WidgetNoteTitle } from "./WidgetNoteTitle";
import { pageForegroundColor } from "./styleHelpers/pageForegroundColor";

type WidgetNoteProps = {
  note: NoteWeb_note$key;
  isRenderedFromOrg?: boolean;
  pageId?: string;
} & Pick<BaseWidgetProps, "interactive">;

export function NoteWeb(props: WidgetNoteProps) {
  const note = useFragment(
    graphql`
      fragment NoteWeb_note on Note {
        id
        title
        isOrgNote
        noteSize
        editingMode
        pages {
          id
          image {
            id
          }
          includedInSlideshow
          backgroundColor
          ...WidgetNotePage_widgetNotePage
        }
      }
    `,
    props.note
  );
  const locked = note.isOrgNote && !props.isRenderedFromOrg;

  const pageId = props.pageId;
  const { height, width } = useWidgetLayout();
  useNoteSubscriptions(note.id, props.interactive === false);

  const pages = useMemo(() => {
    if (pageId) {
      return note.pages;
    }
    const activePages = note.pages.filter((p) => p.includedInSlideshow);
    if (activePages.length > 0) {
      return activePages;
    }
    return note.pages;
  }, [pageId, note.pages]);
  const theme = useTheme();

  const [_activePage, setActivePage] = useState(0);
  const onNext = useCallback(() => {
    if (!pageId) {
      setActivePage((prevPage) => prevPage + 1);
    }
  }, [pageId]);

  const activePage = useMemo(
    () => _activePage % pages.length,
    [_activePage, pages.length]
  );
  useInterval(onNext, pageId ? null : 12000);

  const conrnerRadius = useRadiusSize("medium");

  const titleColorStyle: {
    enableShadow: boolean;
    color: ColorType;
  } = useMemo(() => {
    const page = pageId
      ? pages.find((page) => page.id === pageId)
      : pages[activePage];

    if (!page) {
      return { enableShadow: false, color: "darkerText" };
    }
    const showLightColor = pageForegroundColor(page) === "LIGHT";
    return {
      enableShadow: showLightColor,
      color: showLightColor ? "lightText" : "darkerText",
    };
  }, [activePage, pageId, pages]);

  return (
    <View
      style={{
        height,
        width,
        backgroundColor: theme.surface,
        borderRadius: conrnerRadius,
        overflow: "hidden",
      }}
    >
      {pages.map((page, i) => (
        <WebPageWrapper
          key={page.id}
          active={pageId ? pageId === page.id : i === activePage}
        >
          <WidgetNotePage page={page} />
        </WebPageWrapper>
      ))}

      <View
        pointerEvents="none"
        style={{ position: "absolute", left: 0, top: 0 }}
      >
        <WidgetNoteTitle
          locked={locked}
          title={note.title}
          enableTextShadow={titleColorStyle.enableShadow}
          textColor={titleColorStyle.color}
        />
      </View>
      {pages.length > 1 ? (
        <Box
          pointerEvents="none"
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            justifyContent: "center",
          }}
          spaceHorizontal="small"
          spaceTop="xsmall"
        >
          <AHNonAnimtedPageIndicator
            direction="vertical"
            page={pageId ? pages.findIndex((p) => p.id === pageId) : activePage}
            numberOfItems={pages.length}
          />
        </Box>
      ) : null}
    </View>
  );
}
